import { useEffect, useRef, useState } from 'react';

interface ChatLoadingStateProps {
  elapsed?: number;
}

const PIXEL_DELAYS = Array.from({ length: 9 }, (_, index) => {
  const row = Math.floor(index / 3);
  const column = index % 3;
  return (column + Math.abs(row - 1)) * 90;
});

function formatPreciseElapsed(milliseconds: number): string {
  const totalSeconds = Math.max(0, milliseconds) / 1_000;
  if (totalSeconds < 60) return `${totalSeconds.toFixed(1)}s`;
  return `${Math.floor(totalSeconds / 60)}m ${(totalSeconds % 60).toFixed(1)}s`;
}

export function ChatLoadingState({ elapsed = 0 }: ChatLoadingStateProps) {
  const [now, setNow] = useState(() => Date.now());
  const elapsedAnchorRef = useRef({ serverElapsed: elapsed, receivedAt: Date.now() });

  useEffect(() => {
    const receivedAt = Date.now();
    elapsedAnchorRef.current = { serverElapsed: elapsed, receivedAt };
    setNow(receivedAt);
  }, [elapsed]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(interval);
  }, []);

  const displayElapsed =
    elapsedAnchorRef.current.serverElapsed + now - elapsedAnchorRef.current.receivedAt;

  return (
    <div className="flex w-fit items-center gap-2.5 py-1" role="status" aria-live="polite">
      <span
        className="grid shrink-0 grid-cols-[repeat(3,4px)] gap-[1.5px]"
        aria-hidden="true"
      >
        {PIXEL_DELAYS.map((delay, index) => (
          <span
            key={index}
            className="chat-loading-pixel h-1 w-1 rounded-[1px] bg-surface-600 dark:bg-surface-300"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </span>
      <span className="chat-loading-label text-[13px] font-medium">Churning</span>
      <span className="font-mono text-xs tabular-nums text-surface-400 dark:text-surface-500">
        {formatPreciseElapsed(displayElapsed)}
      </span>
    </div>
  );
}
